import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'

interface LoginProps {
  onClose: () => void
  onSwitchToRegister: () => void
  /** Called after successful login (e.g. to redirect to /app when on landing page) */
  onSuccess?: () => void
}

export default function Login({ onClose, onSwitchToRegister, onSuccess }: LoginProps) {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const { signIn } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { error } = await signIn(email, password)

    if (error) {
      // Try to translate common error messages
      if (error.message.includes('Invalid') || error.message.includes('invalid')) {
        setError(t('login.invalidCredentials'))
      } else {
        setError(error.message || t('login.errorOccurred'))
      }
      setLoading(false)
    } else {
      onClose()
      onSuccess?.()
    }
  }

  const handleSendResetLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setResetLoading(true)

    try {
      // Remember which origin requested the reset (www vs non-www matters for PKCE verifier storage)
      localStorage.setItem('th24_reset_origin', window.location.origin)

      const redirectTo = `${window.location.origin}/reset-password`
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, { redirectTo })
      if (error) {
        setError(error.message || t('login.errorOccurred'))
        return
      }

      onClose()
      navigate(`/reset-password?sent=1&email=${encodeURIComponent(resetEmail)}`)
    } catch (e: any) {
      setError(e?.message || t('login.errorOccurred'))
    } finally {
      setResetLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setShowResetPassword(false)
    setResetEmail('')
    setError(null)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl p-6 md:p-8 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto" style={{backgroundColor: '#FBFBFB'}}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">
            {showResetPassword ? t('login.resetPassword') : t('login.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {showResetPassword ? (
          <>
            <form onSubmit={handleSendResetLink} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <p className="text-sm text-gray-600 mb-4">
                  {t('login.resetPasswordDescription')}
                </p>

                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('login.email')}
                  </label>
                  <input
                    id="resetEmail"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t('login.emailPlaceholder')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetLoading ? t('login.sendingLink') : t('login.sendResetLink')}
                </button>

                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 px-4 transition-colors"
                >
                  {t('login.backToLogin')}
                </button>
            </form>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.password')}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={t('login.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="mt-2 text-sm text-orange-500 hover:text-orange-600 font-medium"
                >
                  {t('login.forgotPassword')}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('login.signingIn') : t('login.signInButton')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {t('login.noAccount')}{' '}
                <button
                  onClick={onSwitchToRegister}
                  className="text-orange-500 hover:text-orange-600 font-medium"
                >
                  {t('login.signUpLink')}
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
